import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Download, Receipt } from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, SectionTitle, LoadingState, ErrorState, StatusBadge } from "@/components/common";
import { QuoteDocument } from "@/modules/quotes/QuoteDocument";
import { usePortal } from "@/pages/portal/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { errMsg, downloadFile } from "@/lib/api";
import { fmtCents, fmtYmd } from "@/lib/format";
import { t } from "@/i18n";

const titleKey = (title) => `invoices.titles.${title.replace(" ", "_").toLowerCase()}`;

export function BalanceCard({ slug, currency }) {
  const { data } = useFetch(`/portal/${slug}/invoices`);
  if (!data) return null;
  return (
    <Card testId="portal-balance-card">
      <Receipt className="h-5 w-5" style={{ color: "var(--brand)" }} />
      <p className="mt-3 font-display text-lg font-bold">{t("portalInvoices.balance")}</p>
      <p className="mt-1 text-2xl font-extrabold" data-testid="portal-balance-due">{fmtCents(data.balance_due_cents, currency)}</p>
      {data.balance_due_cents === 0 && <p className="text-xs text-emerald-700" data-testid="portal-all-paid">{t("portalInvoices.allPaid")}</p>}
      <Link to={`/g/${slug}/account?tab=invoices`} className="mt-2 inline-block text-sm font-semibold underline" style={{ color: "var(--brand)" }} data-testid="portal-view-invoices">{t("portalInvoices.viewAll", { n: data.items.filter((i) => i.kind === "invoice").length })}</Link>
    </Card>
  );
}

export function InvoicesTab({ slug, currency }) {
  const { data } = useFetch(`/portal/${slug}/invoices`);
  if (!data) return <LoadingState />;
  if (!data.items.length) return <p className="text-sm text-slate-500">{t("portalInvoices.none")}</p>;
  return (
    <div className="space-y-2" data-testid="portal-invoices-tab">
      <p className="text-sm">{t("portalInvoices.balance")}: <strong data-testid="portal-invoices-balance">{fmtCents(data.balance_due_cents, currency)}</strong></p>
      {data.items.map((i) => (
        <Link key={i.id} to={`/g/${slug}/invoices/${i.id}`} className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 text-sm hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between" data-testid={`portal-invoice-row-${i.number}`}>
          <span><span className="font-mono font-semibold">{i.number}</span> · {fmtYmd(i.issue_date)}{i.kind === "credit_note" && <span className="ml-1 text-xs text-slate-500">{t("invoices.forInvoice", { number: i.invoice_number })}</span>}</span>
          <span className="flex flex-wrap items-center gap-2"><strong>{i.kind === "credit_note" ? "−" : ""}{fmtCents(i.total_cents, currency)}</strong>{i.balance_cents > 0 && <span className="text-xs text-amber-700">{t("invoices.balance", { amount: fmtCents(i.balance_cents, currency) })}</span>}<StatusBadge status={i.status} /></span>
        </Link>
      ))}
    </div>
  );
}

export default function PortalInvoice() {
  const { id } = useParams();
  const { garage, slug } = usePortal();
  const { me } = useAuth();
  const { data, error, loading, reload } = useFetch(`/portal/${slug}/invoices/${id}`);
  if (me === null) return <Navigate to={`/g/${slug}/login`} replace />;
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const ident = { ...garage, ...(data.garage || {}) };
  const meta = data.kind === "credit_note" ? [[t("invoices.date"), fmtYmd(data.issue_date)], [t("invoices.forInvoiceLabel"), data.invoice_number]]
    : [[t("invoices.issueDate"), fmtYmd(data.issue_date)], [t("invoices.dueDate"), fmtYmd(data.due_date)]];
  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="portal-invoice">
      <Link to={`/g/${slug}/account?tab=invoices`} className="inline-flex items-center gap-1 text-sm text-slate-600"><ArrowLeft className="h-4 w-4" /> {t("workshop.back")}</Link>
      <div className="flex justify-end"><Button variant="outline" onClick={() => downloadFile(`/portal/${slug}/invoices/${data.id}/pdf`).catch((e) => toast.error(errMsg(e)))} data-testid="portal-invoice-pdf"><Download className="mr-2 h-4 w-4" /> PDF</Button></div>
      <QuoteDocument quote={data} garage={ident} title={t(titleKey(data.title))} meta={meta} />
      {data.kind === "invoice" && (
        <Card testId="portal-invoice-payments">
          <SectionTitle>{t("invoices.payments")}</SectionTitle>
          <p className="mb-3 text-sm">{data.balance_cents < 0 ? t("invoices.refundDue") : t("invoices.balanceDue")}: <strong data-testid="portal-invoice-balance">{fmtCents(Math.abs(data.balance_cents), garage.currency)}</strong></p>
          {data.payments.length === 0 ? <p className="text-sm text-slate-500">{t("invoices.noPayments")}</p> : data.payments.map((p) => (
            <p key={p.id} className="flex justify-between border-b border-slate-100 py-2 text-sm" data-testid={`portal-payment-${p.id}`}><span>{fmtYmd(p.date)} · {t(`invoices.methods.${p.method}`)}{p.kind === "refund" && ` · ${t("invoices.refund")}`}</span><strong>{p.kind === "refund" ? "−" : ""}{fmtCents(p.amount_cents, garage.currency)}</strong></p>
          ))}
          {data.credit_notes.map((c) => <Link key={c.id} to={`/g/${slug}/invoices/${c.id}`} className="mt-2 flex justify-between text-sm underline" data-testid={`portal-credit-${c.number}`}><span>{t("invoices.creditNote")} {c.number}</span><span>−{fmtCents(c.total_cents, garage.currency)}</span></Link>)}
          {data.balance_cents > 0 && data.garage?.bank?.bsb && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="portal-invoice-bank">
              <p className="font-semibold">{t("portalInvoices.bankTitle")}</p>
              <p className="mt-1 font-mono text-xs">{t("portalInvoices.bankLine", { name: data.garage.bank.account_name, bsb: data.garage.bank.bsb, number: data.garage.bank.account_number })}</p>
              <p className="mt-1 text-xs text-slate-500">{t("portalInvoices.bankRef", { number: data.number })}</p>
            </div>
          )}
          <p className="mt-3 text-xs text-slate-500">{t("portalInvoices.payAtGarage")}</p>
        </Card>
      )}
    </div>
  );
}
